import type { InterventionSectionUrlParams } from "./intervention-section-context.type";
import type { InterventionSectionItem } from "@/modules/intervention/intervention-section";

import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import {
  getInterventionSectionAssetId,
  getInterventionSectionLayoutIndex,
  getInterventionSectionObservationId,
  getInterventionSectionObservations,
  getInterventionSectionParams,
  getInterventionSectionSections,
} from "@/modules/intervention/intervention-section";
import { INTERVENTION_SECTION_TABS_KEY } from "@/modules/intervention/intervention-section-tabs";
import { getSearchParamNumber } from "@/services/router";

import { INTERVENTION_SECTION_URL_PARAMS } from "./intervention-section-context.constant";

export const buildInterventionSectionSearchParams = ({
  section,
  rootLayoutIndex,
}: {
  section?: InterventionSectionItem;
  rootLayoutIndex?: number;
}): URLSearchParams => {
  // Create empty search params (aka do not bring back existing url params)
  const newParams = new URLSearchParams();

  // Keep details tab to ensure save bar is displayed
  newParams.set(TAB_PARAM_KEYS.CONTEXT_TAB_KEY, TAB_KEYS.INTERVENTIONS_DETAILS);

  if (!section) return newParams;

  const { layoutIndex, assetId, observationId } =
    getInterventionSectionParams(section);

  if (layoutIndex === rootLayoutIndex) return newParams;

  return getSectionSearchParams({
    layoutIndex,
    assetId,
    observationId,
  });
};

export const getSectionSearchParams = (
  newParamsValues: InterventionSectionUrlParams
): URLSearchParams => {
  const newParams = new URLSearchParams();
  const { layoutIndex, assetId, observationId } = newParamsValues;

  // Keep details tab to ensure save bar is displayed
  newParams.set(TAB_PARAM_KEYS.CONTEXT_TAB_KEY, TAB_KEYS.INTERVENTIONS_DETAILS);

  if (observationId) {
    const observationIdKey = INTERVENTION_SECTION_URL_PARAMS.observationId;
    newParams.set(observationIdKey, `${observationId}`);
    return newParams;
  }

  if (layoutIndex) {
    const layoutIndexKey = INTERVENTION_SECTION_URL_PARAMS.layoutIndex;
    newParams.set(layoutIndexKey, `${layoutIndex}`);
  }

  if (assetId) {
    const assetIdKey = INTERVENTION_SECTION_URL_PARAMS.assetId;
    newParams.set(assetIdKey, `${assetId}`);
  }

  return newParams;
};

export const getSectionSearchParamsWithTab = (
  existingParams: URLSearchParams,
  newParamsValues: InterventionSectionUrlParams
): URLSearchParams => {
  const newParams = getSectionSearchParams(newParamsValues);

  // Bring back `sub_tab` if exists
  const paramValue = existingParams.get(INTERVENTION_SECTION_TABS_KEY);
  if (paramValue) newParams.set(INTERVENTION_SECTION_TABS_KEY, paramValue);

  return newParams;
};

export const getInterventionSectionSearchParams = (
  searchParams: URLSearchParams,
  fallbackSection?: InterventionSectionItem
) => {
  return {
    layoutIndex: getLayoutIndexParam(searchParams, fallbackSection),
    assetId: getAssetIdParam(searchParams, fallbackSection),
    observationId: getObservationIdParam(searchParams, fallbackSection),
  };
};

export const getLayoutIndexParam = (
  searchParams: URLSearchParams,
  fallbackSection?: InterventionSectionItem
) => {
  const paramKey = INTERVENTION_SECTION_URL_PARAMS.layoutIndex;
  const layoutIndex = getSearchParamNumber(paramKey, searchParams);
  return layoutIndex ?? getInterventionSectionLayoutIndex(fallbackSection);
};

export const getAssetIdParam = (
  searchParams: URLSearchParams,
  fallbackSection?: InterventionSectionItem
) => {
  const paramKey = INTERVENTION_SECTION_URL_PARAMS.assetId;
  const assetId = getSearchParamNumber(paramKey, searchParams);
  return assetId ?? getInterventionSectionAssetId(fallbackSection);
};

export const getObservationIdParam = (
  searchParams: URLSearchParams,
  fallbackSection?: InterventionSectionItem
) => {
  const paramKey = INTERVENTION_SECTION_URL_PARAMS.observationId;
  const observationId = getSearchParamNumber(paramKey, searchParams);
  return observationId ?? getInterventionSectionObservationId(fallbackSection);
};

export const hasInterventionSectionParams = (
  searchParams: URLSearchParams
): boolean => {
  const { layoutIndex, assetId, observationId } =
    getInterventionSectionSearchParams(searchParams);

  return layoutIndex || assetId || observationId ? true : false;
};

export const getCurrentInterventionSection = ({
  sections,
  urlParams,
  parent,
}: {
  sections: InterventionSectionItem[];
  urlParams: InterventionSectionUrlParams;
  parent?: InterventionSectionItem;
}): {
  section?: InterventionSectionItem;
  parent?: InterventionSectionItem;
} => {
  if (!sections?.length) return {};

  // If no params in the url, there is no current section
  const { layoutIndex, assetId, observationId } = urlParams;
  if (!layoutIndex && !assetId && !observationId) return {};

  // Otherwise let's loop through sections, to find the current one
  for (const section of sections) {
    // If we found a current section, that's it!
    if (isCurrentInterventionSection(section, urlParams)) {
      return { section, parent };
    }

    const subSections = getInterventionSectionSections(section);
    const subObservations = getInterventionSectionObservations(section);
    const children = [...subSections, ...subObservations];

    // If the section has children, let's grab all children and search
    // into it to find the current section.
    if (children.length) {
      const result = getCurrentInterventionSection({
        urlParams,
        sections: children,
        parent: section,
      });

      // If we find the section in the children, return it.
      // Otherwise continue to look into next sections...
      if (result.section) return result;
    }
  }

  // No current section, it's over!
  return {};
};

export const isCurrentInterventionSection = (
  section: InterventionSectionItem,
  urlParams: InterventionSectionUrlParams
): boolean => {
  const { layoutIndex, assetId, observationId } =
    getInterventionSectionParams(section);

  if (urlParams.observationId) {
    return observationId === urlParams.observationId;
  }

  const isCurrentLayoutIndex = layoutIndex === urlParams.layoutIndex;

  if (urlParams.assetId) {
    return assetId === urlParams.assetId && isCurrentLayoutIndex;
  }

  return isCurrentLayoutIndex;
};

export const getRedirectInterventionSection = (
  sections: InterventionSectionItem[],
  urlParams?: InterventionSectionUrlParams
): InterventionSectionItem | undefined => {
  if (!urlParams) return undefined;

  return getCurrentInterventionSection({ sections, urlParams }).section;
};
